import type React from 'react';
import { useState } from 'react';

import Grid from '@mui/material/Grid';
import { useTranslation } from 'react-i18next';

import AvoidanceDialogActions from 'components/dialogs/avoidance/AvoidanceDialogActions';
import { useStyles } from 'components/dialogs/avoidance/styles';
import TMXDialog from 'components/dialogs/TMXDialog';
import TMXInput from 'components/inputs/TMXInput';
import AvoidanceGroupsPanel from 'components/panels/avoidance/AvoidanceGroupsPanel';
import { BodyLargeLight, BodyRegularBold, H3 } from 'components/typography/Typography';
import { type GroupParticipantInterface, type PlainAvoidanceGroupInterface } from 'typedefs/avoidance';
import { participantName } from 'utils/participant';

interface AvoidanceGroupsDialogProps {
  groupName: string;
  isOpen?: boolean;
  isDoubles?: boolean;
  participants?: GroupParticipantInterface[];
  selectedGroup?: PlainAvoidanceGroupInterface;
  changeGroupName: (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => void;
  clearParticipants: () => void;
  createAvoidanceGroup: (
    groupName: string,
    individualParticipantIds: string[],
    selectedGroup: PlainAvoidanceGroupInterface,
  ) => void;
  onClose: () => void;
  setGroupName: (name: string) => void;
  setParticipants: (participants: GroupParticipantInterface[]) => void;
  setSelectedGroup: (selectedGroup: PlainAvoidanceGroupInterface) => void;
}

const AvoidanceGroupsDialog: React.FC<React.PropsWithChildren<AvoidanceGroupsDialogProps>> = ({
  groupName,
  isOpen,
  changeGroupName,
  participants,
  selectedGroup,
  clearParticipants,
  createAvoidanceGroup,
  onClose,
  setGroupName,
  setParticipants,
  setSelectedGroup,
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const paperProps = { style: { padding: 30 } };
  const [filterPlayers, setFilterPlayers] = useState<string>('');
  const checkedParticipantIds = participants
    ?.filter((participant) => participant.checked)
    ?.map((checkedParticipant) => checkedParticipant.id);
  const filteredParticipants = participants
    ?.filter((participant) => participantName(participant).toLowerCase().includes(filterPlayers.toLowerCase()))
    ?.map((participant) => ({
      ...participant,
      checked: checkedParticipantIds.includes(participant.id),
    }));
  const handleSetCheckedParticipants = (participant: GroupParticipantInterface) => {
    const newCheckedParticipants = participants.map((currentParticipant) => {
      if (currentParticipant.id === participant.id) {
        return {
          ...currentParticipant,
          checked: !participant.checked,
        };
      }
      return currentParticipant;
    });
    setParticipants(newCheckedParticipants);
  };
  const handleSaveGroup = () => {
    const ids = checkedParticipantIds
      ?.map((id) => {
        const pair = participants?.find((p) => p.id === id);
        return pair?.individualParticipantIds || [];
      })
      ?.flat();
    createAvoidanceGroup(groupName, ids.length ? ids : checkedParticipantIds, selectedGroup);
    handleOnClose();
  };
  const handleOnChangeFilter = (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    setFilterPlayers(event.target.value);
  };
  const handleOnClose = () => {
    onClose();
    setSelectedGroup(null);
    setGroupName('');
    setFilterPlayers('');
  };

  return (
    <TMXDialog fullWidth open={isOpen} PaperProps={paperProps} onClose={onClose}>
      <Grid container direction="column">
        <Grid item>
          <H3>{t('playerGroup')}</H3>
        </Grid>
        <Grid item>
          <BodyLargeLight>{t('avoidanceGroupsDialog.bodyLarge')}</BodyLargeLight>
        </Grid>
        <Grid item className={classes.groupNameWrapper}>
          <BodyRegularBold>{t('groupName')}</BodyRegularBold>
        </Grid>
        <Grid item>
          <TMXInput fullWidth value={groupName} onChange={changeGroupName} />
        </Grid>
        <AvoidanceGroupsPanel
          participants={filteredParticipants}
          setParticipants={handleSetCheckedParticipants}
          onChangeFilter={handleOnChangeFilter}
        />
        <Grid item className={classes.actionsWrapper}>
          <AvoidanceDialogActions
            cancelAction={handleOnClose}
            clearParticipants={clearParticipants}
            groupName={groupName}
            numberOfSelected={checkedParticipantIds?.length}
            saveGroup={handleSaveGroup}
          />
        </Grid>
      </Grid>
    </TMXDialog>
  );
};

export default AvoidanceGroupsDialog;
